<template>
  <div
    class="flex"
    :class="
      inline
        ? 'flex-row items-center gap-4'
        : 'max-h-[calc(100vh-5rem)] flex-col gap-6 overflow-y-auto pb-6'
    "
  >
    <div
      class="flex flex-row flex-wrap gap-1"
      :class="[sectionPadding, inline && 'grow', !inline && 'mb-4']"
    >
      <BaseMenu
        v-if="kompetenzen.length"
        :items="
          Object.entries(groupedKompetenzen).map(([key, value]) => ({
            label: value[0].kompetenzbereich.label,
            items: value.map((kompetenz) => ({
              id: kompetenz.id,
              title: kompetenz.title,
              selected: selectedKompetenzIds.includes(kompetenz.id),
              color: colorForUeberfachlicheKompetenz(kompetenz),
              leadingIcon: selectedKompetenzIds.includes(kompetenz.id) ? 'check' : undefined,
              callback: () => toggleKompetenz(kompetenz.id),
            })),
          }))
        "
        v-slot="{ open }"
      >
        <BaseTextButton
          color="tonal"
          toggle
          :selected="selectedKompetenzIds.length > 0"
          :trailing-icon="open ? 'expand_less' : 'expand_more'"
          :force-pressed="open"
          >Kompetenzen
          <span v-if="selectedKompetenzIds.length">({{ selectedKompetenzIds.length }})</span>
        </BaseTextButton>
      </BaseMenu>
      <BaseMenu
        v-if="grades.length"
        :items="
          grades.map((grade) => ({
            id: String(grade),
            title: gradeLabel(grade),
            selected: selectedGrades.includes(grade),
            leadingIcon: selectedGrades.includes(grade) ? 'check' : undefined,
            callback: () => emit('toggleGrade', grade),
          }))
        "
        v-slot="{ open }"
      >
        <BaseTextButton
          color="tonal"
          toggle
          :selected="selectedGrades.length > 0"
          :trailing-icon="open ? 'expand_less' : 'expand_more'"
          :force-pressed="open"
          >{{ $t('lumia.lesson.classesLabel') }}
          <span v-if="selectedGrades.length">({{ selectedGrades.length }})</span>
        </BaseTextButton>
      </BaseMenu>
      <BaseMenu
        v-if="subjects.length"
        :items="
          subjects.map((subject) => ({
            id: subject.code,
            title: subject.name,
            selected: selectedSubjectCodes.includes(subject.code),
            leadingIcon: selectedSubjectCodes.includes(subject.code) ? 'check' : undefined,
            callback: () => emit('toggleSubject', subject.code),
          }))
        "
        v-slot="{ open }"
      >
        <BaseTextButton
          color="tonal"
          toggle
          :selected="selectedSubjectCodes.length > 0"
          :trailing-icon="open ? 'expand_less' : 'expand_more'"
          :force-pressed="open"
          >{{ $t('lumia.filters.subjects') }}
          <span v-if="selectedSubjectCodes.length">({{ selectedSubjectCodes.length }})</span>
        </BaseTextButton>
      </BaseMenu>
      <BaseTextButton
        color="tonal"
        icon="bookmark"
        toggle
        :selected="bookmarkedOnly"
        @click="emit('toggleBookmarked')"
        >{{ $t('lumia.tabs.bookmarks') }}</BaseTextButton
      >
    </div>
    <div class="flex flex-col md:hidden" :class="[sectionPadding]">
      <hr v-if="!inline" class="text-outline-variant mb-4 w-full" />
      <BaseTextButton
        class="self-start"
        color="tonal"
        size="XS"
        icon="filter_alt_off"
        @click="emit('reset')"
      >
        {{ $t('lumia.filters.reset') }}
      </BaseTextButton>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import type { LessonImpulseFilterKompetenz } from '../models/lesson-impulse-filters'
  import type { LessonImpulseCodeName } from '../models/lesson-impulse'
  import { colorForUeberfachlicheKompetenz } from '../models/ueberfachliche-kompetenz-color'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'

  interface Props {
    kompetenzen: ReadonlyArray<LessonImpulseFilterKompetenz>
    grades: ReadonlyArray<number>
    subjects: ReadonlyArray<LessonImpulseCodeName>
    selectedGrades: ReadonlyArray<number>
    selectedSubjectCodes: ReadonlyArray<string>
    bookmarkedOnly?: boolean
    inline?: boolean
  }

  const props = defineProps<Props>()

  const sectionPadding = computed(() => (props.inline ? '' : 'px-6'))

  const selectedKompetenzIds = defineModel<string[]>('selectedKompetenzIds', { required: true })

  const toggleKompetenz = (id: string) => {
    if (selectedKompetenzIds.value.includes(id)) {
      selectedKompetenzIds.value = selectedKompetenzIds.value.filter(
        (selectedId) => selectedId !== id,
      )
    } else {
      selectedKompetenzIds.value.push(id)
    }
  }

  const emit = defineEmits<{
    toggleGrade: [grade: number]
    toggleSubject: [code: string]
    toggleBookmarked: []
    reset: []
  }>()

  const { t } = useI18n()

  const gradeLabel = (grade: number) => t('lumia.gradeLabels', { classes: `${grade}.` })

  const groupedKompetenzen = computed(() => {
    return props.kompetenzen.reduce(
      (acc, kompetenz) => {
        acc[kompetenz.kompetenzbereich.key] = [
          ...(acc[kompetenz.kompetenzbereich.key] || []),
          kompetenz,
        ]
        return acc
      },
      {} as Record<string, LessonImpulseFilterKompetenz[]>,
    )
  })
</script>
